import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import IconButton from '@mui/material/IconButton';
import { styled } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';

const TOCContainer = styled(Box)(({ theme }) => ({
  position: 'sticky',
  top: theme.spacing(12),
  maxHeight: 'calc(100vh - 200px)',
  overflowY: 'auto',
  backgroundColor: '#1a1a2e',
  borderRadius: '12px',
  padding: theme.spacing(2),
  border: '1px solid rgba(102, 126, 234, 0.1)',
  '&::-webkit-scrollbar': {
    width: '4px',
  },
  '&::-webkit-scrollbar-track': {
    background: '#16213e',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#667eea',
    borderRadius: '4px',
    '&:hover': {
      background: '#5a67d8',
    },
  },
}));

const TOCItem = styled(ListItemButton)(({ theme, level, active }) => {
  // Normalize indentation since content starts with h3
  const normalizedLevel = level > 2 ? level - 2 : 0;
  
  return {
    paddingLeft: theme.spacing(1 + normalizedLevel * 2),
    paddingRight: theme.spacing(1),
    borderRadius: '8px',
    marginBottom: theme.spacing(0.5),
    transition: 'all 0.2s ease',
    position: 'relative',
    ...(active && {
      background: 'linear-gradient(135deg, #667eea15 0%, #764ba215 100%)',
      '&::before': {
        content: '""',
        position: 'absolute',
        left: 0,
        top: '50%',
        transform: 'translateY(-50%)',
        width: '4px',
        height: '70%',
        background: 'linear-gradient(180deg, #667eea 0%, #764ba2 100%)',
        borderRadius: '2px',
      },
    }),
    '&:hover': {
      background: active 
        ? 'linear-gradient(135deg, #667eea30 0%, #764ba230 100%)' 
        : 'rgba(102, 126, 234, 0.1)',
    },
  };
});

const SectionCheckbox = styled(IconButton)(({ theme }) => ({
  padding: theme.spacing(0.5),
  marginRight: theme.spacing(0.5),
  '&:hover': {
    background: 'rgba(102, 126, 234, 0.1)',
  },
}));

export default function TableOfContents({ content, containerRef, completedSections = [], onToggleSection }) {
  const [headings, setHeadings] = useState([]);
  const [activeId, setActiveId] = useState('');

  useEffect(() => {
    if (!content) return;

    // Extract headings from markdown content
    const lines = content.split('\n');
    const extractedHeadings = [];
    
    lines.forEach((line, index) => {
      const h1Match = line.match(/^#\s+(.+)$/);
      const h2Match = line.match(/^##\s+(.+)$/);
      const h3Match = line.match(/^###\s+(.+)$/);
      const h4Match = line.match(/^####\s+(.+)$/);
      
      let text = null;
      let level = null;
      
      if (h1Match) {
        text = h1Match[1];
        level = 1;
      } else if (h2Match) {
        text = h2Match[1];
        level = 2;
      } else if (h3Match) {
        text = h3Match[1];
        level = 3;
      } else if (h4Match) {
        text = h4Match[1];
        level = 4;
      }
      
      if (text) {
        // Remove markdown formatting like ** from the text
        text = text.replace(/\*\*/g, '').trim();
        
        extractedHeadings.push({
          id: `heading-${index}`,
          text: text,
          level: level,
        });
      }
    });

    setHeadings(extractedHeadings);
  }, [content]);

  useEffect(() => {
    if (!containerRef?.current) return;

    const observerOptions = {
      rootMargin: '-100px 0px -70% 0px',
      threshold: 0,
    };

    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveId(entry.target.id);
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    // Observe all headings
    const container = containerRef.current;
    const headingElements = container.querySelectorAll('h1, h2, h3, h4');
    
    headingElements.forEach((heading, index) => {
      heading.id = `heading-${index}`;
      observer.observe(heading);
    });

    return () => {
      headingElements.forEach((heading) => {
        observer.unobserve(heading);
      });
    };
  }, [containerRef, content]);

  const handleClick = (headingId) => {
    const element = document.getElementById(headingId);
    if (element) {
      const yOffset = -100;
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  if (headings.length === 0) return null;

  return (
    <TOCContainer>
      <List sx={{ p: 0 }}>
        {headings.map((heading) => {
          const isCompleted = completedSections.includes(heading.id);
          
          return (
            <ListItem key={heading.id} disablePadding sx={{ display: 'flex', alignItems: 'center' }}>
              {onToggleSection && (
                <SectionCheckbox
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleSection(heading.id);
                  }}
                  color={isCompleted ? "primary" : "default"}
                >
                  {isCompleted ? (
                    <CheckCircleIcon fontSize="small" />
                  ) : (
                    <RadioButtonUncheckedIcon fontSize="small" />
                  )}
                </SectionCheckbox>
              )}
              <TOCItem
                level={heading.level}
                active={activeId === heading.id}
                onClick={() => handleClick(heading.id)}
                sx={{ flex: 1 }}
              >
                <ListItemText 
                  primary={heading.text}
                  primaryTypographyProps={{
                    fontSize: heading.level <= 3 ? '0.95rem' : '0.875rem',
                    fontWeight: heading.level === 3 ? 700 : heading.level === 4 ? 600 : 400,
                    color: activeId === heading.id ? '#818cf8' : isCompleted ? '#64748b' : '#cbd5e1',
                    textDecoration: isCompleted ? 'line-through' : 'none',
                    opacity: isCompleted ? 0.8 : 1,
                    sx: {
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    },
                  }}
                />
              </TOCItem>
            </ListItem>
          );
        })}
      </List>
    </TOCContainer>
  );
}